import React from "react";
import { motion } from "framer-motion";
import { Activity, Award, Waves, Flag, Goal, Target, Users, Trophy } from "lucide-react";
import { SPORTS } from "./data";
import { PHOTOS } from "./data";
import { resolveFileUrl } from "./fileUrl";

// Custom horse silhouette icon (lucide doesn't include one)
const HorseIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 21v-7l-2-1 3-3 2-4 4-1 4 4 3 3v9" />
    <path d="M5 14h4" />
    <path d="M14 7l-1 -3" />
    <circle cx="15" cy="9" r="0.6" fill="currentColor" />
  </svg>
);

const IconFor = ({ name, className }) => {
  switch (name) {
    case "horse": return <HorseIcon className={className} />;
    case "waves": return <Waves className={className} />;
    case "flag": return <Flag className={className} />;
    case "goal": return <Goal className={className} />;
    case "target": return <Target className={className} />;
    case "activity": return <Activity className={className} />;
    case "users": return <Users className={className} />;
    default: return <Trophy className={className} />;
  }
};

const Sports = () => {
  return (
    <section id="sports" className="relative py-28 px-6 md:px-10 overflow-hidden">
      {/* Cinematic dark backdrop accent */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#d4af37]/[0.06] blur-[160px] pointer-events-none" />

      <div className="max-w-[1300px] mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">05 / Sport &amp; Discipline</div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Built at <span className="italic">dawn</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Sport was never the destination — it was the laboratory. The arena where character compounded one ride, one lap, one repetition at a time.
            </p>
          </div>
        </motion.div>

        {/* Hero — Horse riding */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative rounded-[28px] overflow-hidden border border-white/10 bg-gradient-to-br from-[#0c0d12] via-[#0a0b10] to-[#06070b] p-10 md:p-16 mb-16"
        >
          <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(212,175,55,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(59,130,246,0.1), transparent 40%)" }} />
          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
                <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#e6c769]">National Level</span>
              </div>
              <h3 className="mt-6 text-[44px] md:text-[68px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Horse <span className="italic">Riding</span>.
              </h3>
              <p className="mt-6 max-w-md text-[15px] md:text-[16px] leading-[1.75] text-white/65">
                Pre-dawn stables. Dressage on cold mornings. National competitions that taught me what unflinching focus actually feels like. Discipline, when shared with an animal twice your weight, becomes a quiet philosophy.
              </p>
              <div className="mt-8 flex flex-wrap gap-6">
                {[
                  { l: "2017 — 2020", t: "Years" },
                  { l: "National", t: "Highest level" },
                  { l: "Dressage · Jumping", t: "Disciplines" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-[12px] tracking-[0.3em] uppercase text-white/40">{s.t}</div>
                    <div className="mt-1 text-[18px] text-white font-medium">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-[24px] border border-white/10 bg-gradient-to-br from-[#1a1410] to-black grid place-items-center overflow-hidden">
                <img src={resolveFileUrl(PHOTOS.horseRiding)} alt="Equestrian practice at JIRS" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-white/70 font-mono">Equestrian · archive</div>
                <div className="absolute bottom-5 right-5 text-[10px] tracking-[0.3em] uppercase text-[#e6c769] font-mono">JIRS · Bengaluru</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Other sports */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SPORTS.filter((s) => s.icon !== "horse").map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              viewport={{ once: true }}
              className="group relative rounded-[20px] border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] backdrop-blur-xl p-6 hover:border-[#e6c769]/40 transition-all hover:-translate-y-1"
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 grid place-items-center text-[#e6c769] group-hover:bg-[#e6c769]/10 transition-colors">
                  <IconFor name={s.icon} className="w-5 h-5" />
                </div>
                <Award className="w-4 h-4 text-white/30 group-hover:text-[#e6c769] transition-colors" />
              </div>
              <h4 className="mt-6 text-[22px] text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.name}</h4>
              <div className="mt-2 text-[11px] tracking-[0.3em] uppercase text-white/40 font-mono">{s.years}</div>
              <div className="mt-3 text-[12px] text-[#e6c769]/80">{s.level}</div>
              <p className="mt-4 text-[13px] text-white/55 leading-relaxed">{s.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Sports;
